//
// Copyright (c) 2011-2018 by Curalate, Inc.
//

import angular, { IRootElementService, ISCEService } from 'angular';
import _ from 'underscore';
import URL from 'url';

export class ShowroomMobilePreview {
  public wysiwygMobilePreviewUrl: string;
  public showIframe: boolean;
  public iframeIsLoaded: boolean;
  public previewDomain: string;
  public previewSectionStates;
  public firstProductPanelVisit: boolean;

  public trackingCategory: string;
  public trackingLabels;

  constructor(
        private $element: IRootElementService,
        private $sce: ISCEService,
        private ShowroomService,
        private TrackingService,
        private UniversalImageActionsService,
        private UniversalImageActionsView,
        private SHOWROOM_PREVIEW_SECTIONS,
        private SHOWROOM_PREVIEW_TOGGLE_STATES,
        private SHOWROOM_PREVIEW_DEFAULT_STRINGS,
    ) { }

  public syncPreview = () => {
    const self = this;
    const iframe = this.$element.find('#showroom-mobile-preview-uia');
    iframe.on('load', () => {
      self.iframeIsLoaded = true;
      self.ShowroomService.syncPreviewSections();
      this.TrackingService.recordEvent(
                this.trackingCategory,
                this.ShowroomService.getTrackingAction(),
                this.trackingLabels.loadPreview,
            );
    });
  }

  public $onInit() {
    const launchOptions = this.UniversalImageActionsService.getCurrentLaunchOptions();
    const universalItemId = launchOptions.getUniversalItemId();

    this.trackingCategory = this.ShowroomService.getTrackingCategory();
    this.trackingLabels = {
      initializePreview: 'InitializeShowroomPreview',
      loadPreview: 'LoadShowroomPreview',
    };

    this.TrackingService.recordEvent(
            this.trackingCategory,
            this.ShowroomService.getTrackingAction(),
            this.trackingLabels.initializePreview,
        );

    this.showIframe = false;
    this.iframeIsLoaded = false;
    this.firstProductPanelVisit = true;

        // Initially all sections are hidden
    this.previewSectionStates = {};
    this.previewSectionStates[this.SHOWROOM_PREVIEW_SECTIONS.PRODUCTS] = this.SHOWROOM_PREVIEW_TOGGLE_STATES.HIDDEN;
    this.previewSectionStates[this.SHOWROOM_PREVIEW_SECTIONS.MEDIA] = this.SHOWROOM_PREVIEW_TOGGLE_STATES.HIDDEN;
    this.previewSectionStates[this.SHOWROOM_PREVIEW_SECTIONS.EMAIL_CAPTURE] =
            this.SHOWROOM_PREVIEW_TOGGLE_STATES.HIDDEN;

    this.ShowroomService.getCurrentConfigStatusObservable().subscribe((state) => {
      if (!state.wysiwygMobilePreviewUrl) {
        return;
      }
      this.wysiwygMobilePreviewUrl = this.$sce.trustAsResourceUrl(state.wysiwygMobilePreviewUrl);
      this.setPreviewDomain(state.wysiwygMobilePreviewUrl);

            // When the state updates, see if we need to scroll to a preview section
      if (this.iframeIsLoaded &&
                state.previewSectionToScrollTo &&
                state.previewSectionToScrollTo !== this.SHOWROOM_PREVIEW_SECTIONS.NONE
            ) {
        this.scrollTo(state.previewSectionToScrollTo, this.getLandingPageSourceId(state.impl));
        this.ShowroomService.resetPreviewSectionToScrollTo();
      }

            // When the state updates, see if we need to start the loading state
      if (this.iframeIsLoaded &&
                state.previewSectionToToggleLoad &&
                state.previewSectionToToggleLoad !== this.SHOWROOM_PREVIEW_SECTIONS.NONE
            ) {
        this.startRefreshContent(state.previewSectionToToggleLoad);
        this.ShowroomService.resetPreviewSectionToToggleLoad();
      }

            // When the state updates, see if we need to refresh a preview section
      if (this.iframeIsLoaded &&
                state.previewSectionToUpdate &&
                state.previewSectionToUpdate !== this.SHOWROOM_PREVIEW_SECTIONS.NONE
            ) {
        this.reloadContent(
                    state.previewSectionToUpdate,
                    this.getLandingPageSourceId(state.impl),
                    undefined,
                );
        this.ShowroomService.resetPreviewSectionToRefresh();
      }

            // When the state updates, see if we need to refresh preview text
      if (this.iframeIsLoaded &&
                state.previewTextToUpdate &&
                state.previewTextToUpdate !== this.SHOWROOM_PREVIEW_SECTIONS.NONE
            ) {
        this.refreshStaticContent(
                    state.previewTextToUpdate,
                    this.getLandingPageSourceId(state.impl),
                    this.generatePostDataForState(state),
                );
        this.ShowroomService.resetPreviewTextToRefresh();
      }

      this.showIframe = true;

      if (this.iframeIsLoaded) {
                // When the state updates, see if we need to toggle a preview section
        this.checkAndToggleSections(state.previewToggleStates, this.getLandingPageSourceId(state.impl));
      }
    });

    this.ShowroomService.loadExistingState(universalItemId);
  }

  private generatePostDataForState = (newState) => {
    let customString;
    let postData = {};

    switch (newState.previewTextToUpdate) {
      case this.SHOWROOM_PREVIEW_SECTIONS.EMAIL_CAPTURE: {
        customString = newState?.emailCaptureState?.callToAction;
        postData = { prompt: customString || this.SHOWROOM_PREVIEW_DEFAULT_STRINGS.EMAIL_CTA_DEFAULT };
        break;
      }
      case this.SHOWROOM_PREVIEW_SECTIONS.TAGGED_PRODUCTS: {
        customString = newState?.customStringsState?.shopTheLookHeader;
        postData = { text: customString || this.SHOWROOM_PREVIEW_DEFAULT_STRINGS.TAGGED_PRODUCTS_DEFAULT };
        break;
      }
      case this.SHOWROOM_PREVIEW_SECTIONS.MEDIA: {
        customString = newState?.customStringsState?.relatedItemsHeader;
        postData = { text: customString || this.SHOWROOM_PREVIEW_DEFAULT_STRINGS.RELATED_MEDIA_DEFAULT };
        break;
      }
      case this.SHOWROOM_PREVIEW_SECTIONS.PRODUCTS: {
        customString = newState?.customStringsState?.recommendedProductsHeader;
        postData = { text: customString || this.SHOWROOM_PREVIEW_DEFAULT_STRINGS.RECOMMENDED_PRODUCTS_DEFAULT };
        break;
      }
      default: {
                // Do nothing
      }
    }

    return postData;
  }

  private postToIframe = (postMessage) => {
    const iframe = this.$element.find('#showroom-mobile-preview-uia')[0] as HTMLIFrameElement;
    iframe.contentWindow.postMessage(postMessage, this.previewDomain);
  }

  private scrollTo = (section, sourceId) => {
    const postMessage = {
      name: 'SHOWROOM_SCROLL_TO',
      sourceIdentifier: sourceId,
      target: '',
    };

    switch (section) {
      case this.SHOWROOM_PREVIEW_SECTIONS.MEDIA: {
        postMessage.target = 'related-items-carousel';
        break;
      }
      case this.SHOWROOM_PREVIEW_SECTIONS.PRODUCTS: {
        postMessage.target = 'recommendations-list';
        break;
      }
      case this.SHOWROOM_PREVIEW_SECTIONS.EMAIL_CAPTURE: {
        postMessage.target = 'user-detail-capture';
        break;
      }
      default: {
                // Do nothing
      }
    }

    this.postToIframe(postMessage);
  }

  private toggleSection = (section, sourceId, hideContent, hidePlaceholder) => {
    const postMessage = {
      name: 'SHOWROOM_TOGGLE_CONTENT',
      sourceIdentifier: sourceId,
      target: '',
      hideContent,
      hidePlaceholder,
    };

    switch (section) {
      case this.SHOWROOM_PREVIEW_SECTIONS.MEDIA: {
        postMessage.target = 'related-items-carousel';
        break;
      }
      case this.SHOWROOM_PREVIEW_SECTIONS.PRODUCTS: {
        postMessage.target = 'recommendations-list';
        break;
      }
      case this.SHOWROOM_PREVIEW_SECTIONS.EMAIL_CAPTURE: {
        postMessage.target = 'user-detail-capture';
        break;
      }
      default: {
                // Do nothing
      }
    }

    this.postToIframe(postMessage);
  }

  private reloadContent = (section, sourceId, postData) => {
    const postMessage = _({
            name: 'SHOWROOM_RELOAD_CONTENT',
            sourceIdentifier: sourceId,
            target: '',
        }).extend(postData);

    switch (section) {
      case this.SHOWROOM_PREVIEW_SECTIONS.MEDIA: {
        postMessage.target = 'related-items-carousel';
        break;
      }
      case this.SHOWROOM_PREVIEW_SECTIONS.PRODUCTS: {
        postMessage.target = 'recommendations-list';
        break;
      }
      case this.SHOWROOM_PREVIEW_SECTIONS.EMAIL_CAPTURE: {
        postMessage.target = 'user-detail-capture';
        break;
      }
      default: {
                // do nothing
      }
    }

    this.postToIframe(postMessage);
  }

  private refreshStaticContent = (section, sourceId, postData) => {
    const postMessage = _({
            name: 'SHOWROOM_RELOAD_CONTENT',
            sourceIdentifier: sourceId,
            target: '',
        }).extend(postData);

    switch (section) {
      case this.SHOWROOM_PREVIEW_SECTIONS.EMAIL_CAPTURE: {
        postMessage.target = 'user-detail-capture';
        break;
      }
      case this.SHOWROOM_PREVIEW_SECTIONS.TAGGED_PRODUCTS: {
        postMessage.target = 'shop-the-look-header';
        break;
      }
      case this.SHOWROOM_PREVIEW_SECTIONS.MEDIA: {
        postMessage.target = 'related-items-header';
        break;
      }
      case this.SHOWROOM_PREVIEW_SECTIONS.PRODUCTS: {
        postMessage.target = 'recommended-products-header';
        break;
      }
      default: {
                // do nothing
      }
    }

    this.postToIframe(postMessage);
  }

  private startRefreshContent = (section) => {
    const postMessage = {
      name: 'SHOWROOM_TOGGLE_REFRESH_STATE',
      target: '',
    };

    switch (section) {
      case this.SHOWROOM_PREVIEW_SECTIONS.MEDIA: {
        postMessage.target = 'related-items-carousel';
        break;
      }
      case this.SHOWROOM_PREVIEW_SECTIONS.PRODUCTS: {
        postMessage.target = 'recommendations-list';
        break;
      }
      default: {
                // do nothing
      }
    }

    this.postToIframe(postMessage);
  }

  private checkAndToggleSections = (serviceStates, sourceId) => {
    const currentWorkflow = this.UniversalImageActionsService.getCurrentWorkflow();
    const currentState = currentWorkflow.getCurrentState();

    const productPanel = this.UniversalImageActionsView.TributaryCustomizeProducts.name;
    const mediaPanel = this.UniversalImageActionsView.TributaryCustomizeMedia.name;
    const addOnsPanel = this.UniversalImageActionsView.TributaryAddOns.name;
    const linksPanel = this.UniversalImageActionsView.TributaryCustomizeLinks.name;

    const safelyToggleSection = (previewSection, hidePlaceholder, forceHideContent) => {
      const serviceStateIsValid = _(this.SHOWROOM_PREVIEW_TOGGLE_STATES).contains(serviceStates[previewSection]);
      const hideContent =
                !!forceHideContent || serviceStates[previewSection] === this.SHOWROOM_PREVIEW_TOGGLE_STATES.HIDDEN;
      if (serviceStateIsValid) {
        this.previewSectionStates[previewSection] = serviceStates[previewSection];
        this.toggleSection(previewSection, sourceId, hideContent, hidePlaceholder);
      }
    };

    if (currentState !== productPanel) {
      this.firstProductPanelVisit = false;
    }

        // Display placeholders for a section only if we're on the relevant panel.
        // Note: We special case the recommended products panel, to hide media content on the first run through.
    if (currentState === productPanel) {
      safelyToggleSection(this.SHOWROOM_PREVIEW_SECTIONS.PRODUCTS, false, false);
      safelyToggleSection(this.SHOWROOM_PREVIEW_SECTIONS.MEDIA, true, this.firstProductPanelVisit);
      safelyToggleSection(this.SHOWROOM_PREVIEW_SECTIONS.EMAIL_CAPTURE, true, this.firstProductPanelVisit);
    } else if (currentState === mediaPanel) {
      safelyToggleSection(this.SHOWROOM_PREVIEW_SECTIONS.PRODUCTS, true, false);
      safelyToggleSection(this.SHOWROOM_PREVIEW_SECTIONS.MEDIA, false, false);
      safelyToggleSection(this.SHOWROOM_PREVIEW_SECTIONS.EMAIL_CAPTURE, true, false);
    } else if (currentState === addOnsPanel) {
      safelyToggleSection(this.SHOWROOM_PREVIEW_SECTIONS.PRODUCTS, true, false);
      safelyToggleSection(this.SHOWROOM_PREVIEW_SECTIONS.MEDIA, true, false);
      safelyToggleSection(this.SHOWROOM_PREVIEW_SECTIONS.EMAIL_CAPTURE, false, false);
    } else if (currentState === linksPanel) {
      safelyToggleSection(this.SHOWROOM_PREVIEW_SECTIONS.PRODUCTS, true, false);
      safelyToggleSection(this.SHOWROOM_PREVIEW_SECTIONS.MEDIA, true, false);
      safelyToggleSection(this.SHOWROOM_PREVIEW_SECTIONS.EMAIL_CAPTURE, true, false);
    }
  }

  private setPreviewDomain = (url) => {
    let previewUrl;
    if (url) {
      previewUrl = URL.parse(url);
      this.previewDomain = `${previewUrl.protocol}//${previewUrl.hostname}`;
    }
  }

  private getLandingPageSourceId = (impl) => {
    return impl.landingPageSourceId || impl.sourceId;
  }
}

angular.module('uia').component('showroomMobilePreview', {
  template: require('@legacy-components/common/universal-image-actions/showroom-mobile-preview.html'),
  controller: [
    '$element',
    '$sce',
    'ShowroomService',
    'TrackingService',
    'UniversalImageActionsService',
    'UniversalImageActionsView',
    'SHOWROOM_PREVIEW_SECTIONS',
    'SHOWROOM_PREVIEW_TOGGLE_STATES',
    'SHOWROOM_PREVIEW_DEFAULT_STRINGS',
    ShowroomMobilePreview,
  ],
});
